// CRM layer — client screen (added 6-B, reworked in 6-D).
//
// Two modes in one screen:
//   DIRECTORY — the default. A browsable, searchable list of every saved client
//               with their active-opportunity count. This is what agents land on.
//   PROFILE   — one client: editable details, their opportunities, their calls,
//               plus any pre-CRM `contacts` record surfaced read-only.
//
// The phone box stays as a quick jump-to/create shortcut, but it is no longer
// the only way in.

import React, { useCallback, useEffect, useState } from 'react';
import { FiArrowRight, FiSearch, FiUsers } from 'react-icons/fi';
import {
  crmListClients, crmGetClient, crmSaveClient, crmLookupOldContact, crmNav,
  STAGE_LABEL, stagePillClass, fmtHours, fmtDateTime, typeLabel
} from './crmApi';
import CrmTransferModal from './CrmTransferModal';
import CrmStartOpportunityModal from './CrmStartOpportunityModal';

const CLASSIFICATION = { vip: 'عميل مميز', normal: 'عادي', blacklist: 'قائمة سوداء' };
// Calm identity (Batch 6): the same three classifications, rendered as small
// soft token pills instead of inline colour pairs.
const CLASS_PILL = {
  vip: 'crm-pill crm-pill-warn',
  normal: 'crm-pill crm-pill-neutral',
  blacklist: 'crm-pill crm-pill-danger'
};
const CALL_STATUS = {
  answered: 'تم الرد', missed: 'لم يتم الرد', abandoned: 'أغلق المتصل',
  voicemail: 'بريد صوتي', after_hours: 'خارج الدوام', no_answer: 'لا يوجد رد'
};

const CrmClientPage = ({ setActivePage }) => {
  const [phone, setPhone] = useState(crmNav.getClient());
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [oldContact, setOldContact] = useState(null);

  // directory state
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [classification, setClassification] = useState('');
  const [jump, setJump] = useState('');

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (classification) params.classification = classification;
      const res = await crmListClients(params);
      setList(res.data.clients);
    } catch (e) {
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, [search, classification]);

  const loadProfile = useCallback(async (p) => {
    if (!p) return;
    try {
      const res = await crmGetClient(p);
      setData(res.data);
      setForm(res.data.client || {});
      crmLookupOldContact(res.data.phone_number)
        .then((c) => setOldContact(c.data && c.data.id ? c.data : null))
        .catch(() => setOldContact(null));
    } catch (e) {
      setData(null);
    }
  }, []);

  useEffect(() => {
    if (phone) loadProfile(phone);
    else loadList();
  }, [phone, loadProfile, loadList]);

  const openClient = (p) => { crmNav.setClient(p); setPhone(p); setTab('profile'); };
  const backToList = () => { crmNav.setClient(''); setPhone(''); setData(null); setOldContact(null); };
  const openOpportunity = (id) => { crmNav.setOpportunity(id); setActivePage('crm-opportunity'); };

  const save = async () => {
    try {
      await crmSaveClient(phone, {
        full_name: form.full_name || '', email: form.email || '', address: form.address || '',
        city: form.city || '', classification: form.classification || 'normal',
        source: form.source || '', general_notes: form.general_notes || ''
      });
      setSaved('تم حفظ بيانات العميل');
      setTimeout(() => setSaved(''), 2500);
      loadProfile(phone);
    } catch (e) {
      setSaved(e.response?.data?.error || 'فشل الحفظ');
    }
  };

  const field = (k) => ({ value: form[k] || '', onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  // =========================================================================
  // DIRECTORY
  // =========================================================================
  if (!phone) {
    return (
      // Batch 6 (Group A): calm identity. Styling only — search, classification
      // filter, quick-jump and row navigation are unchanged.
      <div className="crm-scope crm-calm crm-calm-page">
        <div className="page-header">
          <h1 className="page-title"><FiUsers style={{ marginLeft: 8 }} />ملفات العملاء</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="crm-count">{list.length} عميل</span>
            <input className="filter-input" style={{ width: 170 }} placeholder="رقم جديد / انتقال سريع"
              value={jump} onChange={(e) => setJump(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && jump.trim()) openClient(jump.trim()); }} />
            <button className="btn btn-primary btn-sm" disabled={!jump.trim()} onClick={() => openClient(jump.trim())}>
              فتح
            </button>
          </div>
        </div>

        <div className="crm-toolbar">
          <div style={{ position: 'relative' }}>
            <FiSearch className="crm-searchico" />
            <input className="filter-input" style={{ paddingRight: 32, width: 240 }}
              placeholder="بحث بالاسم أو رقم الجوال..."
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="filter-select" value={classification} onChange={(e) => setClassification(e.target.value)}>
            <option value="">كل التصنيفات</option>
            {Object.entries(CLASSIFICATION).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {(search || classification) && (
            <button className="btn btn-outline btn-sm" onClick={() => { setSearch(''); setClassification(''); }}>
              مسح
            </button>
          )}
        </div>

        <div className="table-container">
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>العميل</th><th>رقم الجوال</th><th>التصنيف</th><th>المدينة</th>
                <th>فرص نشطة</th><th>إجمالي الفرص</th><th></th>
              </tr></thead>
              <tbody>
                {listLoading ? (
                  <tr><td colSpan="7" className="crm-empty">جاري التحميل...</td></tr>
                ) : list.length === 0 ? (
                  <tr><td colSpan="7" className="crm-empty">
                    {search || classification ? 'لا توجد نتائج مطابقة' : 'لا يوجد عملاء محفوظون بعد'}
                  </td></tr>
                ) : list.map((c) => (
                  <tr key={c.id} className="crm-rowlink" onClick={() => openClient(c.phone_number)}>
                    <td style={{ fontWeight: 600 }}>{c.full_name || 'بدون اسم'}</td>
                    <td style={{ direction: 'ltr', textAlign: 'right' }}>{c.phone_number}</td>
                    <td>
                      <span className={CLASS_PILL[c.classification] || 'crm-pill crm-pill-neutral'}>
                        {CLASSIFICATION[c.classification] || c.classification}
                      </span>
                    </td>
                    <td>{c.city || '—'}</td>
                    <td>
                      {c.active_opportunities > 0
                        ? <span className="crm-pill crm-pill-accent">{c.active_opportunities}</span>
                        : <span className="crm-faint">0</span>}
                    </td>
                    <td className="crm-muted">{c.total_opportunities}</td>
                    <td><button className="btn btn-outline btn-sm">فتح الملف</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // PROFILE
  // =========================================================================
  if (!data) {
    return (
      <div className="crm-scope crm-calm crm-calm-page">
        <div className="page-header"><h1 className="page-title">ملف العميل</h1></div>
        <div className="table-container"><div className="crm-empty">
          جاري التحميل...
        </div></div>
      </div>
    );
  }

  return (
    <div className="crm-scope crm-calm crm-calm-page">
      <div className="page-header">
        <div>
          <button className="btn btn-outline btn-sm" style={{ marginBottom: 8 }} onClick={backToList}>
            <FiArrowRight size={14} /> كل العملاء
          </button>
          <h1 className="page-title" style={{ marginBottom: 2 }}>
            {(data.client && data.client.full_name) || 'عميل غير مسجل'}
            {data.client && data.client.classification && (
              <span className={CLASS_PILL[data.client.classification] || 'crm-pill crm-pill-neutral'}
                style={{ marginRight: 8 }}>
                {CLASSIFICATION[data.client.classification]}
              </span>
            )}
          </h1>
          <div className="crm-muted" style={{ direction: 'ltr', textAlign: 'right', fontSize: 13 }}>{data.phone_number}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setShowTransfer(true)}>تحويل متابعة</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowStart(true)}>+ فرصة جديدة</button>
        </div>
      </div>

      <div className="table-container">
        <div className="crm-tabs">
          {[['profile', 'بيانات العميل'], ['opps', `الفرص (${data.opportunities.length})`], ['calls', `المكالمات (${data.calls.length})`]]
            .map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`crm-tab${tab === k ? ' on' : ''}`}>{label}</button>
            ))}
        </div>

        {tab === 'profile' && (
          <div style={{ padding: 20 }}>
            {!data.client && (
              <div className="crm-softwarn">
                لا يوجد ملف لهذا الرقم بعد — الحفظ سينشئه. لا يمكن فتح طلب قبل حفظ اسم العميل.
              </div>
            )}

            {oldContact && (
              <div className="crm-oldcontact">
                <div className="h">
                  بيانات محفوظة في سجل العملاء القديم
                </div>
                <div className="row">
                  <div>الاسم: <b>{[oldContact.firstName, oldContact.lastName].filter(Boolean).join(' ') || '—'}</b></div>
                  {oldContact.company && <div>الشركة: <b>{oldContact.company}</b></div>}
                  {oldContact.createdByName && <div>حفظه: <b>{oldContact.createdByName}</b></div>}
                </div>
                {oldContact.notes && (
                  <div className="note">
                    ملاحظات: {oldContact.notes}
                  </div>
                )}
                <div className="acts">
                  {!form.full_name && [oldContact.firstName, oldContact.lastName].filter(Boolean).length > 0 && (
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 12 }}
                      onClick={() => setForm({ ...form, full_name: [oldContact.firstName, oldContact.lastName].filter(Boolean).join(' ') })}>
                      نسخ الاسم للملف
                    </button>
                  )}
                  {oldContact.notes && !form.general_notes && (
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 12 }}
                      onClick={() => setForm({ ...form, general_notes: oldContact.notes })}>
                      نسخ الملاحظات
                    </button>
                  )}
                </div>
                <div className="fine">
                  السجل القديم للقراءة فقط ولم يُحذف — لا يزال متاحاً عبر <code>/api/contacts</code>.
                </div>
              </div>
            )}

            <div className="crm-form-grid">
              <div className="crm-field"><label>الاسم الكامل *</label>
                <input className="filter-input" style={{ width: '100%' }} {...field('full_name')} /></div>
              <div className="crm-field"><label>البريد الإلكتروني</label>
                <input className="filter-input" style={{ width: '100%' }} {...field('email')} /></div>
              <div className="crm-field"><label>المدينة</label>
                <input className="filter-input" style={{ width: '100%' }} {...field('city')} /></div>
              <div className="crm-field"><label>مصدر العميل</label>
                <input className="filter-input" style={{ width: '100%' }} {...field('source')} /></div>
              <div className="crm-field full"><label>العنوان</label>
                <input className="filter-input" style={{ width: '100%' }} {...field('address')} /></div>
              <div className="crm-field"><label>التصنيف</label>
                <select className="filter-select" style={{ width: '100%' }}
                  value={form.classification || 'normal'}
                  onChange={(e) => setForm({ ...form, classification: e.target.value })}>
                  {Object.entries(CLASSIFICATION).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></div>
              <div className="crm-field full"><label>ملاحظات عامة</label>
                <textarea className="filter-input" style={{ width: '100%', minHeight: 80 }} {...field('general_notes')} /></div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="btn btn-primary" onClick={save}>حفظ البيانات</button>
              {saved && <span className="crm-txt-success" style={{ fontSize: 13, fontWeight: 600 }}>{saved}</span>}
            </div>
          </div>
        )}

        {tab === 'opps' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th>نوع الطلب</th><th>المرحلة</th><th>الموظف</th><th>القسم</th>
                <th>مدة المرحلة</th><th>التذكيرات</th><th>التفاصيل</th><th></th>
              </tr></thead>
              <tbody>
                {data.opportunities.length === 0 ? (
                  <tr><td colSpan="8" className="crm-empty">لا توجد فرص</td></tr>
                ) : data.opportunities.map((o) => (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 600 }}>{typeLabel(o.opportunity_type)}{o.has_unread && <span className="crm-unread" title="تعليق داخلي جديد" style={{ marginRight: 6 }}><span className="crm-unread-dot" /> تعليق جديد</span>}</td>
                    <td><span className={stagePillClass(o.crm_stage)}>{STAGE_LABEL[o.crm_stage]}</span></td>
                    <td>{o.assigned_to_extension || <span className="crm-txt-danger">غير مسند</span>}</td>
                    <td>{o.department || '—'}</td>
                    <td>{fmtHours(o.hoursInStage)}</td>
                    <td>{o.reminder_count || 0}</td>
                    <td className="crm-muted" style={{ fontSize: 12, maxWidth: 240 }}>{(o.request_details || '-').slice(0, 70)}</td>
                    <td><button className="btn btn-outline btn-sm" onClick={() => openOpportunity(o.id)}>فتح</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'calls' && (
          <div style={{ padding: 16 }}>
            {data.calls.length === 0 ? (
              <div className="crm-empty">لا توجد مكالمات مسجلة</div>
            ) : data.calls.map((c) => (
              <div key={c.id} className="crm-callrow">
                <span className={`dir ${c.direction === 'inbound' ? 'in' : 'out'}`}>
                  {c.direction === 'inbound' ? '↙' : '↗'}
                </span>
                <div style={{ flex: 1 }}>
                  <div className="t">
                    {c.direction === 'inbound' ? 'مكالمة واردة' : 'مكالمة صادرة'}
                    <span className="s" style={{ marginRight: 6 }}>· {CALL_STATUS[c.status] || c.status}</span>
                  </div>
                  <div className="s">
                    {c.answered_by ? `رد عليها ${c.answered_by} (${c.answered_by_extension || ''})` : 'لم يرد عليها أحد'}
                    {c.queue_name ? ` · ${c.queue_name}` : ''}
                  </div>
                </div>
                <div className="when">{fmtDateTime(c.started_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showTransfer && (
        <CrmTransferModal phoneNumber={phone} onClose={() => setShowTransfer(false)} onDone={() => loadProfile(phone)} />
      )}
      {showStart && (
        <CrmStartOpportunityModal phoneNumber={phone} onClose={() => setShowStart(false)}
          onCreated={() => loadProfile(phone)} onOpenOpportunity={openOpportunity} />
      )}
    </div>
  );
};

export default CrmClientPage;
